// ModIQ Data Service - Firebase integration for saving/loading project data
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from './firebaseConfig';

const COLLECTION_NAME = 'modiq_projects';
const LOCAL_STORAGE_KEY = 'modiq_projects';

// Generate unique project ID
const generateProjectId = () => {
  return `modiq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to serialize complex data structures for Firebase
const serializeForFirebase = (data) => {
  // Convert undefined to null (Firebase doesn't support undefined)
  if (data === undefined) {
    return null;
  }
  
  if (data === null) {
    return data;
  }
  
  if (Array.isArray(data)) {
    // Convert array to object with numeric keys to avoid nested array issues
    const serialized = {};
    data.forEach((item, index) => {
      serialized[index] = serializeForFirebase(item);
    });
    return {
      __isArray: true,
      __length: data.length,
      __data: serialized
    };
  }
  
  if (typeof data === 'object') {
    const serialized = {};
    Object.keys(data).forEach(key => {
      serialized[key] = serializeForFirebase(data[key]);
    });
    return serialized;
  }
  
  return data;
};

// Helper function to deserialize data from Firebase
const deserializeFromFirebase = (data) => {
  if (data === null || data === undefined) {
    return data;
  }
  
  // Handle our special serialized array format (from Firebase)
  if (typeof data === 'object' && data.__isArray) {
    // Reconstruct array from serialized format
    const array = new Array(data.__length);
    Object.keys(data.__data).forEach(key => {
      const index = parseInt(key);
      array[index] = deserializeFromFirebase(data.__data[key]);
    });
    return array;
  }
  
  // IMPORTANT: Check if it's already an array (from localStorage/JSON.parse)
  // Arrays must be handled BEFORE generic objects to preserve array type
  if (Array.isArray(data)) {
    return data.map(item => deserializeFromFirebase(item));
  }
  
  if (typeof data === 'object') {
    const deserialized = {};
    Object.keys(data).forEach(key => {
      deserialized[key] = deserializeFromFirebase(data[key]);
    });
    return deserialized;
  }
  
  return data;
};

// LOCAL STORAGE FALLBACK FUNCTIONS
const saveToLocalStorage = (projectData, projectId = null) => {
  try {
    const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    const id = projectId || generateProjectId();
    
    const completeProjectData = {
      ...projectData,
      projectId: id,
      createdAt: projectData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    projects[id] = completeProjectData;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(projects));
    
    console.log('✅ ModIQ project saved to local storage:', id);
    return { success: true, projectId: id, data: completeProjectData };
  } catch (error) {
    console.error('❌ Error saving to local storage:', error);
    return { success: false, error: error.message };
  }
};

const loadFromLocalStorage = (projectId) => {
  try {
    const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    const projectData = projects[projectId];
    
    if (projectData) {
      console.log('✅ ModIQ project loaded from local storage:', projectId);
      // Deserialize complex data structures
      const deserializedData = deserializeFromFirebase(projectData);
      return { success: true, data: deserializedData };
    } else {
      console.log('❌ No project found in local storage with ID:', projectId);
      return { success: false, error: 'Project not found' };
    }
  } catch (error) {
    console.error('❌ Error loading from local storage:', error);
    return { success: false, error: error.message };
  }
};

const listLocalStorageProjects = (userId = 'anonymous') => {
  try {
    const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    const userProjects = Object.values(projects)
      .filter(project => (project.userId || 'anonymous') === userId)
      .map(project => ({
        projectId: project.projectId,
        projectName: project.projectName,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        projectAddress: project.projectAddress,
        preview: {
          // Handle both nested structure (from saveModIQProject) and flat structure (from extractModIQState)
          buildingCount: project.buildings?.buildPaths?.length || project.buildPaths?.length || 0,
          totalBoxes: project.buildings?.boxes?.length || project.boxes?.length || 0,
          floorCount: project.buildings?.floorCount || project.floorCount || 1,
          layoutStrategy: project.buildings?.layoutStrategy || project.pcaLayoutStrategy || 'unknown'
        }
      }))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    
    console.log(`✅ Found ${userProjects.length} projects in local storage for user:`, userId);
    console.log('Project data structure sample:', projects ? Object.values(projects)[0] : 'No projects');
    return { success: true, projects: userProjects };
  } catch (error) {
    console.error('❌ Error listing local storage projects:', error);
    return { success: false, error: error.message };
  }
};

const deleteFromLocalStorage = (projectId) => {
  try {
    const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    delete projects[projectId];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(projects));
    
    console.log('✅ ModIQ project deleted from local storage:', projectId);
    return { success: true };
  } catch (error) {
    console.error('❌ Error deleting from local storage:', error);
    return { success: false, error: error.message };
  }
};

const updateLocalStorageProjectName = (projectId, newName) => {
  try {
    const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    if (projects[projectId]) {
      projects[projectId].projectName = newName;
      projects[projectId].updatedAt = new Date().toISOString();
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(projects));
      
      console.log('✅ Project name updated in local storage:', projectId);
      return { success: true };
    } else {
      return { success: false, error: 'Project not found' };
    }
  } catch (error) {
    console.error('❌ Error updating project name in local storage:', error);
    return { success: false, error: error.message };
  }
};

// Save complete ModIQ project data
export const saveModIQProject = async (projectData, projectId = null, userId = 'anonymous') => {
  console.log('📦 saveModIQProject called with:', {
    projectId: projectId,
    userId: userId,
    projectName: projectData.projectName,
    willUpdate: !!projectId,
    willCreate: !projectId
  });
  
  // Use local storage if Firebase is not configured
  if (!isFirebaseConfigured() || !db) {
    console.log('🔄 Using local storage fallback');
    
    const completeProjectData = {
      // Metadata
      projectId: projectId || generateProjectId(),
      projectName: projectData.projectName || `ModIQ Project ${new Date().toLocaleDateString()}`,
      userId: userId,
      version: '1.0',
      
      // Parcel Data
      parcel: {
        coordinates: projectData.parcelCoordinates || null,
        bounds: {
          width: projectData.parcelWidth || 200,
          height: projectData.parcelLength || 150,
          minX: projectData.parcelBounds?.minX || null,
          maxX: projectData.parcelBounds?.maxX || null,
          minY: projectData.parcelBounds?.minY || null,
          maxY: projectData.parcelBounds?.maxY || null
        },
        setbacks: {
          north: projectData.setbackNorth || 10,
          east: projectData.setbackEast || 10,
          south: projectData.setbackSouth || 10,
          west: projectData.setbackWest || 10,
          points: projectData.setbackPoints || null,
          distances: projectData.setbackDistances || [],
          edgeDistances: projectData.edgeSetbackDistances || []
        }
      },
      
      // Building Configuration
      buildings: {
        layoutStrategy: projectData.pcaLayoutStrategy || 'smart-hybrid',
        orientationAngle: projectData.orientationOverride || 0,
        principalAngle: projectData.principalAngle || 0,
        boxDimensions: {
          width: projectData.boxWidth || 13.7,
          length: projectData.boxLength || 55,
          constraints: {
            minWidth: projectData.minBoxWidth || 12.29,
            maxWidth: projectData.maxBoxWidth || 15.7,
            minLength: projectData.minBoxLength || 45,
            maxLength: projectData.maxBoxLength || 68
          }
        },
        buildingPadding: projectData.buildingPadding || 20,
        floorCount: projectData.floorCount || 1,
        hasPodium: projectData.hasPodium || false,
        podiumHeight: projectData.podiumHeight || 10,
        
        // Generated Building Data
        buildPaths: projectData.buildPaths || [],
        boxes: projectData.boxes || [],
        buildableAreaCoords: projectData.buildableAreaCoords || null,
        pcaBuildingData: projectData.pcaBuildingData || null
      },
      
      // Optimizations
      optimizations: {
        selectedOptimizations: projectData.selectedOptimizations || {},
        buildingOptimizations: projectData.buildingOptimizations || {},
        selectedBuildingId: projectData.selectedBuildingId || null
      },
      
      // Unit Mix Configuration (per building)
      unitMix: projectData.buildingSettings || {},
      
      // Advanced Settings
      settings: {
        colors: projectData.colors || {},
        display: {
          showDimensions: projectData.showDimensions ?? true,
          showGrid: projectData.showGrid ?? true,
          gridSize: projectData.gridSize || 10,
          showLivableSpacesTable: projectData.showLivableSpacesTable ?? true
        },
        advanced: {
          components: {
            min: projectData.componentsMin || 135,
            max: projectData.componentsMax || 185
          },
          onSiteConstruction: {
            min: projectData.onSiteMin || 100,
            max: projectData.onSiteMax || 150
          }
        }
      },
      
      // Project Metrics (calculated at save time)
      metrics: projectData.projectMetrics || null
    };
    
    return saveToLocalStorage(completeProjectData, projectId);
  }

  // Original Firebase implementation
  try {
    const id = projectId || generateProjectId();
    
    console.log('🔥 Firebase save operation:', {
      originalProjectId: projectId,
      finalId: id,
      isUpdate: !!projectId,
      isCreate: !projectId
    });
    
    // Prepare complete project data structure
    const completeProjectData = {
      // Metadata
      projectId: id,
      projectName: projectData.projectName || `ModIQ Project ${new Date().toLocaleDateString()}`,
      userId: userId,
      createdAt: projectData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: '1.0',
      
      // Parcel Data
      parcel: {
        coordinates: projectData.parcelCoordinates || null,
        bounds: {
          width: projectData.parcelWidth || 200,
          height: projectData.parcelLength || 150,
          minX: projectData.parcelBounds?.minX || null,
          maxX: projectData.parcelBounds?.maxX || null,
          minY: projectData.parcelBounds?.minY || null,
          maxY: projectData.parcelBounds?.maxY || null
        },
        setbacks: {
          north: projectData.setbackNorth || 10,
          east: projectData.setbackEast || 10,
          south: projectData.setbackSouth || 10,
          west: projectData.setbackWest || 10,
          points: projectData.setbackPoints || null,
          distances: projectData.setbackDistances || [],
          edgeDistances: projectData.edgeSetbackDistances || []
        }
      },
      
      // Building Configuration
      buildings: {
        layoutStrategy: projectData.pcaLayoutStrategy || 'smart-hybrid',
        orientationAngle: projectData.orientationOverride || 0,
        principalAngle: projectData.principalAngle || 0,
        boxDimensions: {
          width: projectData.boxWidth || 13.7,
          length: projectData.boxLength || 55,
          constraints: {
            minWidth: projectData.minBoxWidth || 12.29,
            maxWidth: projectData.maxBoxWidth || 15.7,
            minLength: projectData.minBoxLength || 45,
            maxLength: projectData.maxBoxLength || 68
          }
        },
        buildingPadding: projectData.buildingPadding || 20,
        floorCount: projectData.floorCount || 1,
        hasPodium: projectData.hasPodium || false,
        podiumHeight: projectData.podiumHeight || 10,
        
        // Generated Building Data
        buildPaths: projectData.buildPaths || [],
        boxes: projectData.boxes || [],
        buildableAreaCoords: projectData.buildableAreaCoords || null,
        pcaBuildingData: projectData.pcaBuildingData || null
      },
      
      // Optimizations
      optimizations: {
        selectedOptimizations: projectData.selectedOptimizations || {},
        buildingOptimizations: projectData.buildingOptimizations || {},
        selectedBuildingId: projectData.selectedBuildingId || null
      },
      
      // Unit Mix Configuration (per building)
      unitMix: projectData.buildingSettings || {},
      
      // Advanced Settings
      settings: {
        colors: projectData.colors || {},
        display: {
          showDimensions: projectData.showDimensions ?? true,
          showGrid: projectData.showGrid ?? true,
          gridSize: projectData.gridSize || 10,
          showLivableSpacesTable: projectData.showLivableSpacesTable ?? true
        },
        advanced: {
          components: {
            min: projectData.componentsMin || 135,
            max: projectData.componentsMax || 185
          },
          onSiteConstruction: {
            min: projectData.onSiteMin || 100,
            max: projectData.onSiteMax || 150
          }
        }
      },
      
      // Project Metrics (calculated at save time)
      metrics: projectData.projectMetrics || null
    };
    
    // Serialize complex data structures for Firebase
    const serializedData = serializeForFirebase(completeProjectData);
    
    // Save to Firebase
    const docRef = doc(db, COLLECTION_NAME, id);
    
    if (projectId) {
      // Update existing project - preserve createdAt and only update changed fields
      const updateData = { ...serializedData };
      delete updateData.createdAt; // Don't overwrite creation timestamp
      await updateDoc(docRef, updateData);
      console.log('✅ ModIQ project updated successfully:', id);
    } else {
      // Create new project
      await setDoc(docRef, serializedData);
      console.log('✅ ModIQ project created successfully:', id);
    }
    
    return { success: true, projectId: id, data: serializedData };
    
  } catch (error) {
    console.error('❌ Error saving ModIQ project:', error);
    return { success: false, error: error.message };
  }
};

// Load ModIQ project data
export const loadModIQProject = async (projectId) => {
  // Use local storage if Firebase is not configured
  if (!isFirebaseConfigured() || !db) {
    return loadFromLocalStorage(projectId);
  }

  try {
    const docRef = doc(db, COLLECTION_NAME, projectId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log('✅ ModIQ project loaded successfully:', projectId);
      // Deserialize complex data structures
      const deserializedData = deserializeFromFirebase(data);
      return { success: true, data: deserializedData };
    } else {
      console.log('❌ No project found with ID:', projectId);
      return { success: false, error: 'Project not found' };
    }
  } catch (error) {
    console.error('❌ Error loading ModIQ project:', error);
    return { success: false, error: error.message };
  }
};

// List all projects for a user
export const listUserProjects = async (userId = 'anonymous') => {
  // Use local storage if Firebase is not configured
  if (!isFirebaseConfigured() || !db) {
    return listLocalStorageProjects(userId);
  }

  try {
    // Simplified query without orderBy to avoid index requirement
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const projects = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      projects.push({
        projectId: doc.id,
        projectName: data.projectName,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        projectAddress: data.projectAddress,
        preview: {
          buildingCount: data.buildings?.buildPaths?.length || 0,
          totalBoxes: data.buildings?.boxes?.length || 0,
          floorCount: data.buildings?.floorCount || 1,
          layoutStrategy: data.buildings?.layoutStrategy || 'unknown'
        }
      });
    });
    
    // Sort on client side instead of requiring database index
    projects.sort((a, b) => {
      const aTime = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : new Date(a.updatedAt || 0).getTime();
      const bTime = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : new Date(b.updatedAt || 0).getTime();
      return bTime - aTime;
    });
    
    console.log(`✅ Found ${projects.length} projects for user:`, userId);
    return { success: true, projects };
    
  } catch (error) {
    console.error('❌ Error listing user projects:', error);
    // Fallback to local storage if Firebase fails
    console.log('🔄 Falling back to local storage due to Firebase error');
    return listLocalStorageProjects(userId);
  }
};

// Delete a project
export const deleteModIQProject = async (projectId) => {
  // Use local storage if Firebase is not configured
  if (!isFirebaseConfigured() || !db) {
    return deleteFromLocalStorage(projectId);
  }

  try {
    const docRef = doc(db, COLLECTION_NAME, projectId);
    await deleteDoc(docRef);
    
    console.log('✅ ModIQ project deleted successfully:', projectId);
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error deleting ModIQ project:', error);
    return { success: false, error: error.message };
  }
};

// Update project name
export const updateProjectName = async (projectId, newName) => {
  // Use local storage if Firebase is not configured
  if (!isFirebaseConfigured() || !db) {
    return updateLocalStorageProjectName(projectId, newName);
  }

  try {
    const docRef = doc(db, COLLECTION_NAME, projectId);
    await updateDoc(docRef, {
      projectName: newName,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Project name updated successfully:', projectId);
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error updating project name:', error);
    return { success: false, error: error.message };
  }
};

// Auto-save functionality (debounced)
// IMPORTANT: Only auto-saves existing projects (with a projectId) to prevent creating garbage files
let autoSaveTimeout = null;
export const autoSaveProject = (projectData, projectId, userId = 'anonymous', delay = 2000) => {
  // Safety check: Don't auto-save if no project ID (prevents creating new projects on every change)
  if (!projectId) {
    console.log('🚫 Auto-save skipped: No project ID (project not saved yet)');
    return;
  }
  
  console.log('🤖 Auto-save triggered for existing project:', projectId);
  
  // Clear existing timeout
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
  }
  
  // Set new timeout for auto-save
  autoSaveTimeout = setTimeout(async () => {
    console.log('🔄 Auto-saving ModIQ project:', projectId);
    
    const result = await saveModIQProject(projectData, projectId, userId);
    if (result.success) {
      console.log('✅ Auto-save completed for project:', result.projectId);
    } else {
      console.error('❌ Auto-save failed:', result.error);
    }
  }, delay);
};

// Helper function to extract current state from ModIQ components
export const extractModIQState = (modiqState) => {
  const rawData = {
    // Extract all the state variables from your ModIQ components
    projectName: modiqState.projectName,
    parcelCoordinates: modiqState.parcelCoordinates,
    parcelWidth: modiqState.parcelWidth,
    parcelLength: modiqState.parcelLength,
    setbackNorth: modiqState.setbackNorth,
    setbackEast: modiqState.setbackEast,
    setbackSouth: modiqState.setbackSouth,
    setbackWest: modiqState.setbackWest,
    setbackPoints: modiqState.setbackPoints,
    setbackDistances: modiqState.setbackDistances,
    edgeSetbackDistances: modiqState.edgeSetbackDistances,
    pcaLayoutStrategy: modiqState.pcaLayoutStrategy,
    orientationOverride: modiqState.orientationOverride,
    principalAngle: modiqState.principalAngle,
    boxWidth: modiqState.boxWidth,
    boxLength: modiqState.boxLength,
    minBoxWidth: modiqState.minBoxWidth,
    maxBoxWidth: modiqState.maxBoxWidth,
    minBoxLength: modiqState.minBoxLength,
    maxBoxLength: modiqState.maxBoxLength,
    buildingPadding: modiqState.buildingPadding,
    floorCount: modiqState.floorCount,
    hasPodium: modiqState.hasPodium,
    buildPaths: modiqState.layoutState?.buildPaths,
    boxes: modiqState.layoutState?.boxes,
    buildableAreaCoords: modiqState.buildableAreaCoords,
    pcaBuildingData: modiqState.pcaBuildingData,
    selectedOptimizations: modiqState.layoutState?.selectedOptimizations,
    buildingOptimizations: modiqState.layoutState?.buildingOptimizations,
    selectedBuildingId: modiqState.layoutState?.selectedBuildingId,
    buildingSettings: modiqState.buildingSettings,
    colors: modiqState.colors,
    showDimensions: modiqState.showDimensions,
    showGrid: modiqState.showGrid,
    gridSize: modiqState.gridSize,
    showLivableSpacesTable: modiqState.showLivableSpacesTable,
    projectMetrics: modiqState.projectMetrics,
    
    // Market and pricing data
    selectedMarket: modiqState.selectedMarket,
    customRentOverrides: modiqState.customRentOverrides,
    
    // Project address
    projectAddress: modiqState.projectAddress
  };
  
  // Serialize complex data structures for Firebase
  return serializeForFirebase(rawData);
};

export default {
  saveModIQProject,
  loadModIQProject,
  listUserProjects,
  deleteModIQProject,
  updateProjectName,
  autoSaveProject,
  extractModIQState
}; 